import { CroppingStyleMode, getCropManagementConfig } from "@omnia/shared/crops";
import { Layer } from "@omnia/shared/map/layer";
import { memoizeReactConfig } from "@omnia/shared/react/reactDirective";
import { FieldViewModel, isFieldViewModel } from "../../../fields";
import { CropId } from "../../../generated/enums";
import { ILayer, ILayerOptions, ILayerSettings } from "../../../map/types";
import { str } from "../../../strings";
import { IOmniaSession } from "../../../types";
import { CroppingLayerOptions } from "./CroppingLayerOptions";
import { CroppingLayerStyle } from "./croppingStyle";
import { setFieldsLayerFeatures } from "./fieldsLayer";
import { ol_Feature, ol_geom } from "@omnia/exports/openlayers";

export const ShowFieldNameKey = "showFieldName";

interface CroppingLayerSettings extends ILayerSettings {
    /**
     * Setting to control how the cropping colour should be determined.
     */
    croppingStyleMode: CroppingStyleMode;
    /**
     * Settings to control which labels should be displayed on a cropping.
     */
    croppingLabels: CroppingLayerLabelSettings;
}

/**
 * Settings for which labels should be displayed on a cropping.
 */
export interface CroppingLayerLabelSettings {
    fieldName: boolean;
    fieldNumber: boolean;
    cropName: boolean;
    varietyName: boolean;
    croppingDescription: boolean;
    plotId: boolean;
    sfiManagementOptions: boolean;
    area: boolean;
}

/**
 * The default `CroppingLayerLabelSettings` for a `CroppingLayer`
 */
export const defaultCroppingLayerLabelSettings: CroppingLayerLabelSettings = {
    cropName: true,
    croppingDescription: false,
    fieldName: true,
    fieldNumber: false,
    plotId: false,
    sfiManagementOptions: false,
    varietyName: false,
    area: false,
};

/**
 * The default `CroppingStyleMode` for a `CroppingLayer`.
 */
export const defaultCroppingLayerStyleMode: CroppingStyleMode = "crop";

interface CroppingLayerOptions extends Omit<ILayerOptions, "settings" | "layersPopup"> {
    /**
     * Should the layer be visible by default?
     *
     * (If left `undefined`, default is `true`)
     *
     * @default true
     */
    visible?: boolean;

    /**
     * Should the layer labels be visible by default?
     *
     * (If left `undefined`, default is `true`)
     *
     * @default true
     */
    showLabels?: boolean;

    /**
     * If `true` the layer is ALWAYS visible, and the user may not change the visibility from the map-options popup. (Can still be changed via code)
     *
     * (If left `undefined`, default is `false`)
     *
     * @default false
     */
    alwaysVisible?: boolean;

    /**
     * Whether the layer should be visible & controllable from the "Map Options" popover panel.
     *
     * (If left `undefined`, default is `true`. Note this is different to the *Layer* default.)
     *
     * @default true
     */
    layersPopup?: boolean;

    /**
     * Setting to control how the cropping colour should be determined.
     *
     * (Optional. If not specified, the user's preference will be used.)
     */
    croppingStyleMode?: CroppingStyleMode;

    /**
     * Settings to control which labels should be displayed on a cropping.
     *
     * (Optional. If not specified, the user's preference will be used.)
     */
    croppingLabels?: CroppingLayerLabelSettings;
}

export class CroppingLayer extends Layer<CroppingLayerSettings> {
    public static readonly LOCAL_STORAGE_KEY = "croppings" as const;

    private _style: CroppingLayerStyle;

    private _currentYearId: number;

    public OptionsComponent = memoizeReactConfig(CroppingLayerOptions, () => ({
        layer: this,
    }));

    public showSelectedFillColor: boolean;
    public showSelectedText: boolean;

    public readonly managementOptions: ReturnType<typeof getCropManagementConfig>;

    constructor(
        { visible, showLabels, alwaysVisible, layersPopup, ...options }: CroppingLayerOptions,
        session?: IOmniaSession
    ) {
        super({
            layersPopup: layersPopup ?? true,

            defaultStyleProvider: (selected) => {
                const s = this.getCroppingStyle();
                return s.getLayerStyleFunction(selected);
            },

            ...options,

            settings: {
                visible: visible ?? true,
                showLabels: showLabels ?? true,
                alwaysVisible: alwaysVisible ?? false,
            },
        });

        if (session) {
            this.managementOptions = getCropManagementConfig(session);

            const preferences = session
                .getSettings("map")
                .getSettings("layers")
                .getKey<CroppingLayerSettings>(CroppingLayer.LOCAL_STORAGE_KEY)
                .get(undefined);

            // Respect the order of precedence for these settings.
            // - Explicitly-defined options (options constructor argument)
            // - User preference (localStorage)
            // - Existing settings value (this.settings)
            // - Fallback default value (constants)
            this.settings.croppingLabels =
                options?.croppingLabels ??
                preferences?.croppingLabels ??
                this.settings.croppingLabels ??
                defaultCroppingLayerLabelSettings;

            this.settings.croppingStyleMode =
                options?.croppingStyleMode ??
                preferences?.croppingStyleMode ??
                this.settings.croppingStyleMode ??
                defaultCroppingLayerStyleMode;
        }
    }

    /**
     * Set related properties using the settings object.
     */
    restoreSettings() {
        super.restoreSettings();

        this.visible = !!this.settings?.visible;
        this.showLabels = !!this.settings?.showLabels;

        this.refresh();
    }

    /**
     * Set the settings object using any related properties.
     */
    syncSettings() {
        this.settings = {
            ...this.settings,
            visible: !!this.visible,
            showLabels: !!this.showLabels,
            croppingStyleMode: this.settings?.croppingStyleMode || "crop",
            croppingLabels: {
                ...defaultCroppingLayerLabelSettings,
                ...this.settings?.croppingLabels,
            },
        };

        (this.OptionsComponent as any).update();
        this.refresh();
    }

    public getCroppingStyle() {
        return (this._style = this._style || new CroppingLayerStyle(this));
    }

    getConfigurableLayers(): ILayer[] {
        return [this];
    }

    highlight(crops: FieldViewModel[]) {
        return super.highlightFeaturesWithData(crops, (c) => c.getId());
    }

    get name(): string {
        return str.Cropping;
    }

    get isVisible(): boolean {
        return this._genericLayer.getVisible();
    }

    get visible(): boolean {
        return this._genericLayer.getVisible();
    }

    set visible(value: boolean) {
        super.setVisible(value);
    }

    set isVisible(value: boolean) {
        super.setVisible(value);
    }

    get currentYearId() {
        return this._currentYearId;
    }

    public setCrops(
        crops: FieldViewModel[],
        currentYearId: number,
        geometryProvider?: (from: FieldViewModel) => ol_geom.Geometry
    ) {
        this._currentYearId = currentYearId;

        crops = crops.sort((c1, c2) => c2.area - c1.area);

        const features = setFieldsLayerFeatures(this, crops, geometryProvider);

        setupLabels(features);
    }

    findField(fieldId: number) {
        return this.findFeatures((_, d) => isFieldViewModel(d) && d.fieldId === fieldId);
    }

    findCropping(croppingId: number) {
        const m = this.findFeatures((_, d) => isFieldViewModel(d) && d.croppingId === croppingId);
        return m ? m[0] : undefined;
    }

    findCrops(croppingIds: Iterable<number>) {
        const set = new Set(croppingIds);
        const m = this.findFeatures((_, d) => isFieldViewModel(d) && set.has(d.croppingId));
        return m;
    }

    // Style Mode

    get croppingStyleMode() {
        return this.settings.croppingStyleMode;
    }

    set croppingStyleMode(value) {
        this.settings.croppingStyleMode = value;
        this.syncSettings();
    }

    // Cropping Labels

    get croppingLabels() {
        return this.settings.croppingLabels;
    }

    // Labels

    showLabel(key: keyof typeof this.settings.croppingLabels, value?: boolean) {
        if (value === undefined) {
            return this.settings.croppingLabels[key];
        } else {
            this.settings.croppingLabels[key] = !!value;
            this.syncSettings();

            return this.settings.croppingLabels[key];
        }
    }

    get showFieldNames() {
        return this.showLabel("fieldName");
    }

    set showFieldNames(value: boolean) {
        this.showLabel("fieldName", value);
    }

    get showCropNames() {
        return this.showLabel("cropName");
    }

    set showCropNames(value) {
        this.showLabel("cropName", value);
    }

    get showVarietyNames() {
        return this.showLabel("varietyName");
    }

    set showVarietyNames(value) {
        this.showLabel("varietyName", value);
    }
}

function setupLabels(features: ol_Feature[]) {
    features.forEach((f) => {
        const crop = f.get("data") as FieldViewModel;

        let showFieldName = true;

        // If a crop has siblings, then the field name should only be shown on the largest one
        if (crop.hasSiblings()) {
            const siblings = [...crop.getSiblings(), crop.cropping]
                .filter((c) => c.YearId === crop.yearId && !shouldIgnoreCropInLabels(c.CropId))
                .sort((c1, c2) => c2.Area - c1.Area);

            if (siblings.length && siblings[0].CroppingId != crop.croppingId) {
                showFieldName = false;
            }
        }

        f.set(ShowFieldNameKey, showFieldName);
    });
}

const ignoredCrops = new Set([CropId.GrassMargin]);

export function shouldIgnoreCropInLabels(cropId: number) {
    return ignoredCrops.has(cropId ?? 0);
}
